import { mapGetters } from "vuex";
import Sidesheet from "@/components/sidesheet";
import Input2 from "@/components/controls2/Input";
import Button from "@/components/controls2/Button";
import Switcher2 from "@/components/controls2/Switcher";
import ErrorFeedback from "@/components/error-feedback";
import NumberValue from "@/components/controls/number-value";

import { PRODUCTION_TYPE, DELIVERY_TYPE, DELIVERY_GROUP_CODE } from "@/constants/positionTypes";
import positionWorkloadMixin from "@/mixins/positionWorkloadMixin";

import { formatProductionPosition } from "@/entities/models/position/production-position";
import { formatDeliveryPosition } from "@/entities/models/position/delivery-position";

const ERRORS = {
    required: "Обязательное поле",
};

export default {
    name: "m-filter-constructor",

    components: {
        "c-sidesheet": Sidesheet,
        "c-input-2": Input2,
        "c-button": Button,
        "c-switcher-2": Switcher2,
        ErrorFeedback,
        NumberValue,
    },

    props: ["active"],

    data() {
        return {
            localActive: this.active,
            isSubmitted: false,

            dictionaries: {
                types: [
                    { label: "Производство", value: PRODUCTION_TYPE },
                    { label: "Доставка", value: DELIVERY_TYPE },
                ],
                productionGroups: [],
                routeTypes: [],
            },

            form: {
                name: "",
                code: "",
                type: PRODUCTION_TYPE,
                productionGroup: null,
                fixDailySalary: "",

                routeType: null,
                costPerMin: "",
                costPerOrder: "",
                costPerKm: "",
            },

            ...positionWorkloadMixin.data,

            serverErrors: {
                code: false,
            },
        };
    },

    computed: {
        ...mapGetters("positions", ["getProductionGroups", "getRouteTypes"]),

        ...positionWorkloadMixin.computed,

        isProductionType() {
            return this.form.type === PRODUCTION_TYPE;
        },

        /** VALIDATION * */

        nameError() {
            const value = this.form.name.trim();
            return !value && ERRORS.required;
        },

        codeError() {
            if (this.serverErrors.code) return this.serverErrors.code;
            const code = this.form.code.trim();
            if (!code) return ERRORS.required;
            
            return false;
        },

        productionGroupError() {
            if (!this.isProductionType) return false;
            const { productionGroup } = this.form;
            if (!productionGroup) return ERRORS.required;
            return false;
        },

        fixDailySalaryError() {
            if (!this.isProductionType) return false;
            const value = this.form.fixDailySalary.trim();
            if (!value) return ERRORS.required;
            return false;
        },

        costPerMinError() {
            if (this.isProductionType) return false;
            const value = this.form.costPerMin.trim();
            if (!value) return ERRORS.required;
            return false;
        },

        costPerOrderError() {
            if (this.isProductionType) return false;
            const value = this.form.costPerOrder.trim();
            if (!value) return ERRORS.required;
            return false;
        },

        costPerKmError() {
            if (this.isProductionType) return false;
            const value = this.form.costPerKm.trim();
            if (!value) return ERRORS.required;
            return false;
        },

        isValid() {

            if (this.isProductionType) {
                if (this.nameError || this.codeError || this.productionGroupError || this.fixDailySalaryError) {
                    return false;
                }
                if (!this.isWorkloadValid) {
                    return false;
                }
            } else if (this.costPerMinError || this.costPerOrderError || this.costPerKmError) {
                return false;
            }
            return true;
        },
    },

    watch: {
        "active": function (value) {
            this.localActive = value;
            if (value) this.reset();
        },

        "getProductionGroups": function (value) {
            this.dictionaries.productionGroups = value
                .filter((item) => item.code !== DELIVERY_GROUP_CODE)
                .map((item) => ({ label: item.name, value: item.code }));
        },

        "getRouteTypes": function (value) {
            this.dictionaries.routeTypes = value;
            const [defaultType] = value;
            this.form.routeType = defaultType;
        },

        "form.code": function () {
            this.serverErrors.code = "";
        },

        ...positionWorkloadMixin.watch,
    },

    async created() {
        positionWorkloadMixin.created.call(this);

        this.$store.dispatch("positions/getProductionGroups");
        this.$store.dispatch("positions/getRouteTypes");

        document.getElementsByTagName("body")[0].addEventListener("click", () => {
            this.$root.$emit("bv::hide::popover");
        });

        document.addEventListener("keyup", (evt) => {
            if (evt.keyCode === 27) {
                if (this.active) {
                    this.close();
                }
            }
        });
    },

    methods: {
        close() {
            this.$emit("close");
            this.reset();
        },

        log(evt) {
            window.console.log(evt);
        },

        ...positionWorkloadMixin.methods,

        reset() {
            this.isSubmitted = false;

            this.form.name = "";
            this.form.code = "";
            this.form.type = PRODUCTION_TYPE;
            this.form.productionGroup = null;

            const [defaultRouteType] = this.dictionaries.routeTypes;
            this.form.routeType = defaultRouteType;

            this.form.costPerMin = "";
            this.form.costPerOrder = "";
            this.form.costPerKm = "";

            this.resetWorkload();

            this.form.fixDailySalary = "";

            this.serverErrors.code = false;
        },

        getParams() {
            const {
                name,
                code,
                type,

                productionGroup,
                fixDailySalary,
                routeType,
                costPerMin,
                costPerOrder,
                costPerKm,
            } = this.form;

            let params = {
                name: name.trim(),
                code: code.trim(),
            };

            if (type === PRODUCTION_TYPE) {
                const workloadParamList = this.getWorkloadParams() || {};

                params = {
                    ...params,
                    type: "production",
                    group: productionGroup && productionGroup.value,

                    fixDailySalary,
                    ...workloadParamList,
                };
                params = formatProductionPosition(params);
            } else {
                params = {
                    ...params,
                    type: "delivery",
                    group: DELIVERY_GROUP_CODE,

                    routeType: routeType && routeType.value,
                    costPerMin,
                    costPerOrder,
                    costPerKm,
                };
                params = formatDeliveryPosition(params);
            }

            return params;
        },

        createPositionRequest() {
            const action = this.isProductionType
                ? "positions/createNewPosition"
                : "positions/createNewDeliveryPosition";
            return this.$store.dispatch(action, this.getParams());
        },

        handleServerErrors(errors = {}) {
            console.log({errors})
            if (errors.code) {
                const [error] = errors.code;
                this.serverErrors.code = error;
            }
        },

        submit() {
            this.isSubmitted = true;

            if (this.isProductionType) {
                this.checkWorkloadType();
                this.checkUnitType();
                this.checkProductionStandard();
            }

            if (!this.isValid) return;

            this.createPositionRequest().then((res) => {
                console.log({res})
                if (res && res.errors !== undefined) {
                    this.handleServerErrors(res.errors);
                } else {
                    this.$emit("close");
                    this.reset();
                    this.$emit("saved", res.model);
                }
            }).catch(err => {
                console.log({err})
            });
        },
    },
};
